import React, { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { FaCopy, FaEllipsisV, FaRegEdit } from 'react-icons/fa';
import { numberFormatter } from '../../../../../utils/helpers/numberFormater';
import { Button, Dropdown, Image, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
    copyToClipboard,
    emailRegex,
    getCorrectSocialUrl,
    truncateString,
} from '../../../../../shared/functions/Functions';
import { DiscoveryFavoriteProfile, DiscoveryFavoriteResponse, DiscoveryReactionEnum } from '../dto/IDiscoveryFilter';
import Flag from '../../../../../shared/components/flag/Flag';
import { IAudiencesManual } from '../../../../../authentication/social/dto/IAudiencesManual';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { FaRegTrashCan } from 'react-icons/fa6';
import { DiscoveryService } from '../service';
import { ErrorToast, WarningToast } from '../../../../../utils/toasters';
import { IconArrow, IconDoubleArrow, IconMessage } from '../../../../../shared/Icons';
import { MUTED_TEXT_COLOR } from '../../../../../utils/constants';
import { HiOutlineX } from 'react-icons/hi';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import { FavoritesReusablePopup } from './FavoritesReusablePopup';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import { BrandCollaborationTooltip } from '../../../../../shared/BrandCollaborationTooltip';

interface DiscoveryFavoritesCardProps {
    favorite: DiscoveryFavoriteResponse;
    removeFromFavorites: (ivId: number, externalId: number, name: string) => void;
    addReaction: (listId: number, reaction: DiscoveryReactionEnum) => void;
    openNoteModal: (favorite: DiscoveryFavoriteResponse) => void;
    openClientNoteModal: (favorite: DiscoveryFavoriteResponse) => void;
    setRefreshList: Dispatch<SetStateAction<boolean>>;
    openExternalModal: (
        externalId?: number,
        ivId?: number,
        ivProfileId?: number,
        internalUser?: boolean,
        hasOffer?: boolean,
        offerId?: number
    ) => void;
    isClient?: boolean;
    id: number;
    isGlobal: boolean;
    isBundle?: boolean;
}

const priorityIcons: Record<string, JSX.Element> = {
    prio_a: <IconDoubleArrow width="14" height="14" color="currentColor" />,
    prio_b: <IconArrow width="14" height="14" color="currentColor" />,
    no_prio: <HiOutlineX size={18} color="currentColor" />,
};

const ActionsDropdown: React.FC<any> = React.memo(({ user, onRemove, className, drop = 'down' }) => {
    const { t } = useTranslation();

    const rootClass = ['btn-group position-static', className].filter(Boolean).join(' ');

    return (
        <Dropdown className={rootClass} drop={drop}>
            <Dropdown.Toggle variant="light" id="dropdown-basic" aria-label="Actions">
                <FaEllipsisV className="text-secondary" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Item
                    className="text-danger d-flex align-items-center d-flex"
                    onClick={() =>
                        onRemove(
                            user?.ivId ?? 0,
                            user?.externalId ?? 0,
                            user?.title ? '@' + user?.title : user?.fullName
                        )
                    }
                >
                    <FaRegTrashCan className="mr-1" /> {t('general.button.remove')}
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );
});

export const DiscoveryFavoritesCard: React.FC<DiscoveryFavoritesCardProps> = ({
    favorite,
    removeFromFavorites,
    openNoteModal,
    openClientNoteModal,
    openExternalModal,
    isClient,
    addReaction,
    id,
    isGlobal,
    setRefreshList,
    isBundle,
}) => {
    function getUserFromFavorite(
        favorites: DiscoveryFavoriteResponse | undefined | null
    ): DiscoveryFavoriteProfile | null {
        if (!favorites) {
            return null;
        }
        return favorites.internalUser ?? favorites.externalUser ?? null;
    }

    const brandCollaborations = favorite?.collaboration?.brandCollaborations;
    const { DiscoveryPriorityEnum } = useSelector((states: IAllStates) => states.enums);
    const user = getUserFromFavorite(favorite);
    const { t } = useTranslation();
    const isInternal = Boolean(favorite.internalUser);
    const maleAudience = user?.audiences?.find(aud => aud.dimension === 'gender' && aud.metric === 'male');
    const femaleAudience = user?.audiences?.find(aud => aud.dimension === 'gender' && aud.metric === 'female');
    const canViewPurchaseOrder = useSelector(
        (states: IAllStates) => states.auth.user.company?.canViewPurchaseOrder
    );
    const [additionalState, setAdditionalState] = useState<{
        isEmailPopupOpen: boolean;
        isPricePopupOpen: boolean;
        isPurchaseOrderPopupOpen: boolean;
        type: 'email' | 'price' | 'po' | null;
        email?: string | null;
        price?: number | null;
        po?: string | null;
    }>({
        isEmailPopupOpen: false,
        isPricePopupOpen: false,
        isPurchaseOrderPopupOpen: false,
        type: null,
    });
    const handleAddPriority = async (item: string | null, listId: number) => {
        DiscoveryService.addPriority(listId, item)
            .then(() => {
                setRefreshList((prevState: boolean) => !prevState);
            })
            .catch(err => ErrorToast(err));
    };
    const addEmailApiRequest = async () => {
        if (!emailRegex.test(additionalState?.email as string) && additionalState?.email?.length) {
            return WarningToast(t('general.toasts.warnValidEmail'));
        }
        DiscoveryService.addEmail(favorite?.id, additionalState?.email)
            .then(() => {
                setAdditionalState(prevState => ({
                    ...prevState,
                    isEmailPopupOpen: false,
                    type: null,
                    email: null,
                }));
                setRefreshList(prevState => !prevState);
            })
            .catch(err => ErrorToast(err));
    };
    const addPriceApiRequest = async () => {
        // if (!additionalState?.price) return;
        DiscoveryService.addPrice(favorite.id, additionalState?.price)
            .then(() => {
                setAdditionalState(prevState => ({
                    ...prevState,
                    isPricePopupOpen: false,
                    type: null,
                    price: null,
                }));
                setRefreshList(prevState => !prevState);
            })
            .catch(err => ErrorToast(err));
    };
    const addPurchaseOrderApiRequest = async () => {
        DiscoveryService.addPurchaseOrder(favorite.id, additionalState?.po)
            .then(() => {
                setAdditionalState(prevState => ({
                    ...prevState,
                    isPurchaseOrderPopupOpen: false,
                    type: null,
                    po: null,
                }));
                setRefreshList(prevState => !prevState);
            })
            .catch(err => ErrorToast(err));
    };

    const renderPriority = (listId: number, priority?: string) => {
        return (
            <Dropdown drop="down" className="btn-group position-static">
                <Dropdown.Toggle
                    variant="null"
                    className={`${'dropdown-btn-' + priority} px-2 py-1 text-uppercase`}
                    id="dropdown-basic"
                    aria-label="Actions"
                >
                    {priorityIcons[priority as ''] && (
                        <div className="mr-2 d-inline-block">{priorityIcons[priority as '']}</div>
                    )}

                    <div className="d-inline-block">
                        {priority ? t(`brand.campaign.discovery.${priority}`) : '+ Add'}
                    </div>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {DiscoveryPriorityEnum?.map(item => {
                        return (
                            <Dropdown.Item
                                key={item}
                                className={'d-flex align-items-center dropdown-item-' + item}
                                onClick={() => handleAddPriority(item, listId)}
                            >
                                {priorityIcons[item]}
                                <div className="ml-2 d-inline-block">{t(`brand.campaign.discovery.${item}`)}</div>
                            </Dropdown.Item>
                        );
                    })}
                    {priority && (
                        <Dropdown.Item
                            className="text-danger d-flex align-items-center d-flex"
                            onClick={() => handleAddPriority(null, listId)}
                        >
                            <FaRegTrashCan className="mr-2" /> {t('agency.filter.clear')}
                        </Dropdown.Item>
                    )}
                </Dropdown.Menu>
            </Dropdown>
        );
    };

    const renderEmail = (email?: string) => {
        const truncatedEmail = truncateString(email ?? '', 25);
        const isTruncated = (email?.length ?? 0) > 25;

        const displayEmail = isTruncated ? (
            <TooltipOverlay content={email}>
                <p className="mb-0 text-primary">{truncatedEmail ?? '-'}</p>
            </TooltipOverlay>
        ) : (
            <p className="mb-0 text-primary">{email ?? '-'}</p>
        );

        return (
            <div className="position-relative">
                {isClient ? (
                    <p className="mb-0 text-primary">{displayEmail}</p>
                ) : (
                    <>
                        {!!email ? (
                            <div className="d-flex align-items-center justify-content-center">
                                {/*<FaRegEdit*/}
                                {/*    onClick={() =>*/}
                                {/*        setAdditionalState(prevState => ({*/}
                                {/*            ...prevState,*/}
                                {/*            isEmailPopupOpen: true,*/}
                                {/*            type: 'email',*/}
                                {/*            email: email ?? '',*/}
                                {/*        }))*/}
                                {/*    }*/}
                                {/*    className="text-primary cursor-pointer"*/}
                                {/*    size={18}*/}
                                {/*/>*/}
                                <p
                                    className="mb-0 ps-1 cursor-pointer text-primary"
                                    onClick={() =>
                                        setAdditionalState(prevState => ({
                                            ...prevState,
                                            isEmailPopupOpen: true,
                                            type: 'email',
                                            email: email ?? '',
                                        }))
                                    }
                                >
                                    {displayEmail}
                                </p>
                            </div>
                        ) : (
                            <span
                                onClick={() =>
                                    setAdditionalState(prevState => ({
                                        ...prevState,
                                        isEmailPopupOpen: true,
                                        type: 'email',
                                        email: email ?? '',
                                    }))
                                }
                                className="text-primary text-uppercase font-weight-semi-bold cursor-pointer"
                            >
                                + Add email
                            </span>
                        )}
                    </>
                )}
                <FavoritesReusablePopup
                    isOpen={additionalState.isEmailPopupOpen}
                    type="email"
                    email={additionalState.email}
                    onChange={value => setAdditionalState(prev => ({ ...prev, email: String(value) }))}
                    onClose={() =>
                        setAdditionalState(prevState => ({
                            ...prevState,
                            isEmailPopupOpen: false,
                            type: null,
                            email: '',
                        }))
                    }
                    onSubmit={addEmailApiRequest}
                />
            </div>
        );
    };

    const renderPrice = (price?: number) => {
        return (
            <div className="position-relative">
                {isClient ? (
                    <p className={`text-primary mb-0 ${typeof price === 'number' ? 'euro-symbol' : ''}`}>
                        {typeof price === 'number' ? priceSplitter(price) : '-'}
                    </p>
                ) : (
                    <>
                        {typeof price === 'number' ? (
                            <div className="d-flex align-items-center justify-content-center">
                                {/*<FaRegEdit*/}
                                {/*    onClick={() =>*/}
                                {/*        setAdditionalState(prevState => ({*/}
                                {/*            ...prevState,*/}
                                {/*            isPricePopupOpen: true,*/}
                                {/*            type: 'price',*/}
                                {/*            price: price,*/}
                                {/*        }))*/}
                                {/*    }*/}
                                {/*    className="text-primary cursor-pointer"*/}
                                {/*    size={18}*/}
                                {/*/>*/}
                                <p
                                    className="mb-0 ps-1 text-primary euro-symbol cursor-pointer"
                                    onClick={() =>
                                        setAdditionalState(prevState => ({
                                            ...prevState,
                                            isPricePopupOpen: true,
                                            type: 'price',
                                            price: price,
                                        }))
                                    }
                                >
                                    {priceSplitter(price)}
                                </p>
                            </div>
                        ) : (
                            <span
                                onClick={() =>
                                    setAdditionalState(prevState => ({
                                        ...prevState,
                                        isPricePopupOpen: true,
                                        type: 'price',
                                        price: price,
                                    }))
                                }
                                className="text-primary text-uppercase font-weight-semi-bold cursor-pointer"
                            >
                                + Add price
                            </span>
                        )}
                    </>
                )}
                <FavoritesReusablePopup
                    isOpen={additionalState.isPricePopupOpen}
                    type="price"
                    price={additionalState.price}
                    onChange={value => setAdditionalState(prev => ({ ...prev, price: Number(value) }))}
                    onClose={() =>
                        setAdditionalState(prevState => ({
                            ...prevState,
                            isPricePopupOpen: false,
                            type: null,
                        }))
                    }
                    onSubmit={addPriceApiRequest}
                />
            </div>
        );
    };

    const renderPurchaseOrder = (purchaseOrder?: string) => {
        return (
            <div className="position-relative">
                {!!purchaseOrder ? (
                    <div className="d-flex align-items-center justify-content-center">
                        <p
                            className="mb-0 ps-1 text-primary cursor-pointer"
                            onClick={() =>
                                setAdditionalState(prevState => ({
                                    ...prevState,
                                    isPurchaseOrderPopupOpen: true,
                                    type: 'po',
                                    po: purchaseOrder,
                                }))
                            }
                        >
                            {purchaseOrder}
                        </p>
                    </div>
                ) : (
                    <span
                        onClick={() =>
                            setAdditionalState(prevState => ({
                                ...prevState,
                                isPurchaseOrderPopupOpen: true,
                                type: 'po',
                                po: purchaseOrder,
                            }))
                        }
                        className="text-primary text-uppercase font-weight-semi-bold cursor-pointer"
                    >
                        + Add PO
                    </span>
                )}
                <FavoritesReusablePopup
                    isOpen={additionalState.isPurchaseOrderPopupOpen}
                    type="po"
                    po={additionalState.po}
                    onChange={value => setAdditionalState(prev => ({ ...prev, po: String(value) }))}
                    onClose={() =>
                        setAdditionalState(prevState => ({
                            ...prevState,
                            isPurchaseOrderPopupOpen: false,
                            type: null,
                        }))
                    }
                    onSubmit={addPurchaseOrderApiRequest}
                />
            </div>
        );
    };

    const offerStatusIcon = (offerStatus?: string, isContacted?: boolean, isDeclined?: boolean) => {
        function checkIconAndStatus() {
            if (isDeclined) {
                return 'rejected';
            }
            if (isContacted) {
                return 'contacted';
            }
            switch (offerStatus) {
                case 'pending':
                case 'negotiate':
                    return 'contacted';
                case 'accepted':
                case 'completed':
                    return 'accepted';
                case 'rejected':
                case 'declined':
                case 'canceled':
                case 'expired':
                    return 'rejected';
                default:
                    return 'contacted';
            }
        }

        const icon = (
            <div className="btn btn-icon active">
                <Image
                    alt="Note image"
                    src={`/icons/offer-status/img-offer-${checkIconAndStatus()}.svg`}
                    width={20}
                    height={20}
                />
            </div>
        );

        if (!offerStatus && isInternal) {
            return '';
        }

        return (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip id="impressions-tooltip">
                        <span className="font-weight-semi-bold">
                            {t(`brand.campaign.discovery.offer_${checkIconAndStatus()}`)}
                        </span>
                    </Tooltip>
                }
            >
                {icon}
            </OverlayTrigger>
        );
    };

    const getButtonLabel = () => {
        if (isGlobal) {
            return t('brand.campaign.discovery.profile');
        }
        if (user?.hasOffer && !isClient) {
            return t('brand.campaign.discovery.offer');
        }
        if (!user?.hasOffer && isInternal && !isClient) {
            return t('brand.campaign.discovery.invite');
        }
        return t('brand.campaign.discovery.profile');
    };

    function AudiencesRows({ audiences }: { audiences?: IAudiencesManual[] }) {
        const safeAudiences = (audiences ?? []).filter(aud => aud.dimension === 'country');

        return (
            <>
                {safeAudiences.slice(0, 3).map((aud, index) => (
                    <td key={index} className="align-middle text-center">
                        <div className="d-flex flex-row justify-content-center align-items-center" style={{ gap: 4 }}>
                            <Flag code={aud.metric} className="rounded-circle" width={26} size="1x1" />
                            <div>{priceSplitter(aud.value)}%</div>
                        </div>
                    </td>
                ))}

                {safeAudiences.length < 3 &&
                    Array.from({ length: 3 - safeAudiences.length }).map((_, i) => (
                        <td key={`empty-${i}`} className="align-middle text-center text-muted">
                            <div className="d-flex justify-content-center align-items-center">–</div>
                        </td>
                    ))}
            </>
        );
    }

    return (
        <tr className="bg-white shadow-sm default-radius p-3">
            <td className="mx-2 position-sticky bg-white left-0 pr-0 z-index-2" style={{ zIndex: 1 }}>
                <div className="d-flex align-items-center border-right">
                    <CustomImageHandler
                        altTag={user?.fullName ?? ''}
                        classes="user-img-rounded me-3"
                        thumbnailPath={user?.photoUrl ?? ''}
                        photoPath={user?.photoUrl ?? ''}
                        style={{ width: '50px', height: '50px', borderRadius: '50%' }}
                        name={user?.fullName ?? user?.title}
                    />
                    <div className="mx-2">
                        <strong>{user?.fullName ?? user?.title}</strong>
                        <div className="d-flex flex-nowrap">
                            <a
                                target="_blank"
                                href={`${getCorrectSocialUrl(
                                    user?.mainPlatform?.platformEnum as string,
                                    user?.title ?? ''
                                )}`}
                                className="text-muted cursor-pointer"
                                rel="noopener noreferrer"
                            >
                                @{user?.title}
                            </a>
                            <span>
                                <FaCopy
                                    className="pl-1 cursor-pointer textHover"
                                    onClick={() => copyToClipboard('@' + user?.title)}
                                    size={14}
                                />
                            </span>
                        </div>
                    </div>
                </div>
            </td>
            <td className="text-center">
                <a
                    target="_blank"
                    className="cursor-pointer"
                    rel="noreferrer"
                    href={`${getCorrectSocialUrl(user?.mainPlatform?.platformEnum as string, user?.title ?? '')}`}
                >
                    <CustomImageHandler
                        altTag={user?.mainPlatform?.name ?? ''}
                        photoPath={user?.mainPlatform?.path ?? ''}
                    />
                </a>
            </td>
            <td className="text-center text-nowrap">{renderEmail(favorite?.email)}</td>
            <td className="text-center">{renderPriority(favorite?.id, favorite?.priority)}</td>
            {/*{!isClient && (*/}
            {/*    <td className="text-center">*/}
            {/*        <div className="d-flex justify-content-center w-100">*/}
            {/*            <div*/}
            {/*                onClick={() => removeFromFavorites(user?.ivId ?? 0, user?.externalId ?? 0)}*/}
            {/*                className="icon-container p-2 cursor-pointer bg-white active shadow-sm"*/}
            {/*            >*/}
            {/*                <HeartFillIcon />*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*    </td>*/}
            {/*)}*/}
            {/*<td className="text-center">*/}
            {/*    <FavoritesReactions*/}
            {/*        id={id}*/}
            {/*        addReaction={addReaction}*/}
            {/*        isClient={isClient}*/}
            {/*        response={favorite?.reaction}*/}
            {/*    />*/}
            {/*</td>*/}
            <td className="text-center">{numberFormatter(user?.followers ?? 0)}</td>
            <td className="text-center">{priceSplitter(user?.engagementRate)}%</td>
            {/*<td className="text-center">{numberFormatter(user?.engagements ?? 0)}</td>*/}
            <td className="text-center">{numberFormatter(femaleAudience?.value ?? 0)}%</td>
            <td className="text-center">{numberFormatter(maleAudience?.value ?? 0)}%</td>
            <AudiencesRows audiences={user?.audiences} />
            <td>
                {brandCollaborations?.length ? (
                    <div className="d-flex align-items-center position-relative mr-3">
                        {brandCollaborations?.slice(0, 3).map((brand, i) => (
                            <TooltipOverlay
                                key={brand?.id + i}
                                content={brand?.name}
                                style={{ marginRight: '-8px', width: 26, maxWidth: 26 }}
                            >
                                <img
                                    src={brand?.logoUrl ?? '/icons/UserPlaceholder.png'}
                                    className="campaign-card-img"
                                    alt="Brand logo"
                                    onError={(e: any) => {
                                        e.target.onerror = null;
                                        e.target.src = '/icons/UserPlaceholder.png';
                                    }}
                                />
                            </TooltipOverlay>
                        ))}
                        <div style={{ marginRight: '-8px' }}>
                            <BrandCollaborationTooltip
                                numberVisibleBrands={3}
                                brands={brandCollaborations}
                                styleSpan={{ width: 26, maxWidth: 26 }}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="text-center w-100">-</div>
                )}
            </td>
            <td className="text-center text-nowrap">{renderPrice(favorite?.price)}</td>
            {canViewPurchaseOrder && (
                <td className="text-center text-nowrap">
                    {renderPurchaseOrder(favorite?.influencerPurchaseOrder)}
                </td>
            )}
            {!isGlobal && (
                <td>
                    {!isInternal && !user?.isContacted && !user?.isDeclined
                        ? ''
                        : offerStatusIcon(user?.requestStatus, user?.isContacted, user?.isDeclined)}
                </td>
            )}
            <td onClick={() => openNoteModal(favorite)} className="text-center">
                <div className="btn btn-icon">
                    {!!favorite?.note ? (
                        <IconMessage color={'#05bbc9'} width={'20'} height={'20'} />
                    ) : isClient && favorite?.note === null ? (
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id="impressions-tooltip">
                                    <span className="font-weight-semi-bold mr-2">
                                        {t('brand.campaign.discovery.no_notes')}
                                    </span>
                                </Tooltip>
                            }
                        >
                            <span>
                                <IconMessage color={MUTED_TEXT_COLOR} width={'20'} height={'20'} />
                            </span>
                        </OverlayTrigger>
                    ) : (
                        <IconMessage
                            classes="cursor-pointer"
                            color={MUTED_TEXT_COLOR}
                            width={'20'}
                            height={'20'}
                            // id={`${isClient && favorite?.note === null && 'cursor-initial'}`}
                        />
                    )}
                </div>
            </td>
            {!isGlobal && (
                <td onClick={() => openClientNoteModal(favorite)} className="text-center">
                    <div className="btn btn-icon">
                        {!!favorite?.clientNote ? (
                            <IconMessage color={'#05bbc9'} width={'20'} height={'20'} />
                        ) : !isClient && favorite?.clientNote === null ? (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id="client-note-tooltip">
                                        <span className="font-weight-semi-bold mr-2">
                                            {t('brand.campaign.discovery.no_client_notes')}
                                        </span>
                                    </Tooltip>
                                }
                            >
                                <span>
                                    <IconMessage color={MUTED_TEXT_COLOR} width={'20'} height={'20'} />
                                </span>
                            </OverlayTrigger>
                        ) : (
                            <IconMessage
                                classes="cursor-pointer"
                                color={MUTED_TEXT_COLOR}
                                width={'20'}
                                height={'20'}
                            />
                        )}
                    </div>
                </td>
            )}
            {(isBundle ? !isClient : true) && (
                <td className="text-center">
                    <ActionsDropdown id={id} listData={favorite} onRemove={removeFromFavorites} user={user} />
                </td>
            )}
            <td className="text-end pr-2">
                {/*{favorite?.alreadyViewed && (*/}
                {/*    <OverlayTrigger*/}
                {/*        placement="top"*/}
                {/*        overlay={*/}
                {/*            <Tooltip id="eye-tooltip">*/}
                {/*                <span className="font-weight-semi-bold mr-2">*/}
                {/*                    {t('brand.campaign.discovery.already_seen')}*/}
                {/*                </span>*/}
                {/*            </Tooltip>*/}
                {/*        }*/}
                {/*    >*/}
                {/*        <AiOutlineEye className="mr-2 cursor-pointer" color="grey" size={20} />*/}
                {/*    </OverlayTrigger>*/}
                {/*)}*/}
                <Button
                    onClick={() =>
                        openExternalModal(
                            user?.externalId ?? 0,
                            user?.ivId,
                            user?.ivProfileId,
                            isInternal,
                            user?.hasOffer,
                            user?.offerId
                        )
                    }
                    variant="outline-primary"
                >
                    {getButtonLabel()}
                </Button>
            </td>
        </tr>
    );
};

export default React.memo(DiscoveryFavoritesCard);
